import $ from 'jquery'
import { SLICE_NAMES } from './slice_configurations'

/**
 * Manages tooltips for pie chart slices
 * Handles positioning, content, and visibility of tooltips
 */
export class TooltipManager {
  constructor(tooltipElement, colorSchemes, resultsContainer, isMobileDevice) {
    this.tooltip = $(tooltipElement)
    this.tooltipElement = tooltipElement
    this.colorSchemes = colorSchemes
    this.resultsContainer = resultsContainer
    this.isMobileDevice = isMobileDevice
    
    this.initialize()
  }

  /**
   * Initialize tooltip behavior
   * Sets up global event listeners for positioning and hiding
   */
  initialize() {
    if (this.isMobileDevice) {
      this.tooltipElement.style.display = 'none'
      return
    }

    this.tooltipElement.style.display = 'none'

    // Position tooltip on mousemove over results
    this.resultsContainer.addEventListener('mousemove', (e) => {
      this.tooltipElement.style.left = `${e.pageX + 20}px`
      this.tooltipElement.style.top = `${e.pageY}px`
    })

    // Hide tooltip on click
    document.addEventListener('click', () => {
      this.tooltipElement.style.display = 'none'
    })

    // Hide tooltip on mouseover (outside canvas)
    document.addEventListener('mouseover', () => {
      this.tooltipElement.style.display = 'none'
    })
  }

  /**
   * Setup tooltip for a canvas element
   */
  setupCanvasTooltip(canvas, sectionVertices, data) {
    canvas.addEventListener('mousemove', (evt) => {
      const mousePos = this.getMousePos(canvas, evt)
      this.handleMouseMove(mousePos, sectionVertices, data)
    })
  }

  handleMouseMove(mousePos, sectionVertices, data) {
    const tooltipName = this.tooltip.find('.name')
    const tooltipLine = this.tooltip.find('hr')
    const tooltipDesc = this.tooltip.find('.desc')

    this.tooltip.css('backgroundColor', this.colorSchemes[data.color_scheme].light)
    this.tooltip.css('borderColor', this.colorSchemes[data.color_scheme].dark)
    tooltipLine.css('backgroundColor', this.colorSchemes[data.color_scheme].dark)
    
    tooltipName.empty()
    tooltipDesc.empty()
    this.tooltip.hide()
    tooltipLine.hide()

    // Check if in center circle
    if (this.isInCenterCircle(mousePos)) {
      const type = data.wrap === '1' ? 'wrap' : 'sleeve'
      tooltipName.text(type.charAt(0).toUpperCase() + type.slice(1))
      this.positionTooltip(false)
      this.tooltip.show()
      return
    }

    // Check if in slice
    for (let i = 0; i < sectionVertices.length; i++) {
      if (this.pnpoly(3, sectionVertices[i][0], sectionVertices[i][1], mousePos.x, mousePos.y)) {
        this.tooltip.show()
        this.showSliceTooltip(i, data, tooltipName, tooltipDesc, tooltipLine)
        break
      }
    }
  }

  isInCenterCircle(mousePos) {
    const r = 15
    const x = 15 * Math.sqrt(2) / 2
    const centerPoints = {
      x: [100, 100 - x, 100 - r, 100 - x, 100, 100 + x, 100 + r, 100 + x],
      y: [100 + r, 100 + x, 100, 100 - x, 100 - r, 100 - x, 100, 100 + x]
    }

    return this.pnpoly(8, centerPoints.x, centerPoints.y, mousePos.x, mousePos.y)
  }

  showSliceTooltip(sliceIndex, data, tooltipName, tooltipDesc, tooltipLine) {
    const slice = SLICE_NAMES[sliceIndex]

    switch (sliceIndex) {
      case 0: // Color
        this.positionTooltip(false)
        tooltipName.html(`${slice}: ${data.pie_color}`)
        break
      
      case 1: // Material
        this.positionTooltip(true)
        tooltipName.html(`${data.material_name.toUpperCase()}`)
        tooltipDesc.html(data.description)
        tooltipLine.show()
        break
      
      case 2: // Abrasion
        this.positionTooltip(false)
        if (data.abrasion_cycle_count) {
          tooltipName.html(`${slice}: ${data.abrasion_cycle_count} Cycles`)
        } else {
          const levels = ['Low', 'Low', 'Medium', 'High']
          tooltipName.html(`${slice}: ${levels[data.pie_abrasion] || 'Low'}`)
        }
        break
      
      case 3: // Price
        this.positionTooltip(false)
        let priceLevel = 'Low'
        if (data.cost_distributor >= 5) {
          priceLevel = 'High'
        } else if (data.cost_distributor > 1.5) {
          priceLevel = 'Medium'
        }
        tooltipName.html(`${slice}: ${priceLevel}`)
        break
      
      case 4: // Expansion
        this.positionTooltip(false)
        const expText = data.pie_expansion < 50 ? 'Minimal' : `${data.pie_expansion}%`
        tooltipName.html(`${slice}: ${expText}`)
        break
      
      case 5: // Cutting Tool
        this.positionTooltip(false)
        let tool = 'Cut with '
        switch (data.scissors) {
          case 0:
            tool += 'a hot knife'
            break
          case 1:
            tool += 'scissors'
            break
          case 3:
            tool += 'scissors or a hot knife'
            break
        }
        tooltipName.html(tool)
        break
      
      case 6: // Flexibility
        this.positionTooltip(false)
        const flexLevels = ['Low', 'Low', 'Medium', 'High']
        tooltipName.html(`${slice}: ${flexLevels[data.pie_flexibility] || 'Medium'}`)
        break
      
      case 7: // Temperature
        this.positionTooltip(false)
        if (data.temp_continuous !== '' && data.product_name !== 'vectran' && data.product_name !== 'carbonfiber') {
          tooltipName.html(`${slice}: ${data.temp_continuous}&deg;F`)
        } else if (data.pie_temp > 4999) {
          tooltipName.html('Non-Flammable')
        } else if (data.pie_temp < 200) {
          tooltipName.html('Melts at a low temperature')
        } else {
          tooltipName.html(`Melts at ${data.pie_temp}&deg;F`)
        }
        break
      
      default:
        this.positionTooltip(false)
        tooltipName.html(slice)
    }
  }

  positionTooltip(left) {
    if (left) {
      this.tooltip.css({ 'margin-left': '-13em', 'margin-top': '1.7em' })
    } else {
      this.tooltip.css({ 'margin-left': '0', 'margin-top': '0' })
    }
  }

  // Point in polygon test
  pnpoly(nvert, vertx, verty, testx, testy) {
    let c = false
    for (let i = 0, j = nvert - 1; i < nvert; j = i++) {
      if (((verty[i] > testy) !== (verty[j] > testy)) &&
          (testx < (vertx[j] - vertx[i]) * (testy - verty[i]) / (verty[j] - verty[i]) + vertx[i])) {
        c = !c
      }
    }
    return c
  }

  getMousePos(canvas, evt) {
    const rect = canvas.getBoundingClientRect()
    return {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top
    }
  }
}
